/**
 * Copyright (c) 2025 Adaptive Chaos Labs, LLC
 * All rights reserved.
 */

/**
 * **What we pay a demand source, and what that does to a bid's value** (frame `0.1` slice `3c-0`,
 * `#1341` `#1108`).
 *
 * Todd, 2026-09-16: a source's terms are two concepts, **Cost** (what we pay them) and **Price** (what
 * the auction uses as the gross value). `3c-0` built the Cost half; slice `3c-i` added **Price** as
 * its sibling on the terms, replacing `floorCpm` + `cpmIsFixed`, and **Weight** — the score boost
 * `#937` promised — beside both, because all three are rules about what a bid is worth to us and
 * the console shows each one's arithmetic.
 *
 * Before this, `calculateNetValue` read `terms.cost` as a flat deduction and nothing read
 * `terms.model`, so a 20% rev-share source was scored as if it charged a fixed amount per ad.
 *
 * **Imports nothing**, because the console aliases it for the same rule and the same words
 * (`shared-modules.spec.ts`): the Cost block shows `%` or `$` by type, and its preview of what a bid
 * nets must be the arithmetic the optimizer runs.
 */

/**
 * How the cost is stated. **CPA is not a cost type** (Todd), and *fixed* is a Price, so neither is
 * here. A closed union because we decide what cost types exist.
 */
export type DemandSourceCostType = 'cpm' | 'revShare';

export const DEMAND_SOURCE_COST_TYPES: readonly DemandSourceCostType[] = Object.freeze(['cpm', 'revShare']);

export const DEMAND_SOURCE_COST_TYPE_LABELS: Readonly<Record<DemandSourceCostType, string>> = Object.freeze({
  cpm: 'CPM',
  revShare: 'Rev-share',
});

/** What we pay a demand source. */
export interface DemandSourceCost {
  type: DemandSourceCostType;
  /**
   * A CPM amount for `cpm`, in the same units as the bid. A **percentage of the bid** for `revShare`,
   * stored as the operator types it: `20` is 20%.
   */
  value: number;
  /** ISO-4217. Recorded, not converted — see {@link COST_CURRENCY_NOT_CONVERTED}. */
  currency: string;
}

/**
 * Why the currency cannot be changed. Nothing in bidBrain converts currencies: a bid carries none, and
 * floors and costs are compared with it as numbers. An editable currency would be stored and read by
 * nothing (`#1017`), so the Cost block shows it and says this instead. `#1351` is the work.
 */
export const COST_CURRENCY_NOT_CONVERTED =
  'bidBrain does not convert currencies yet, so the cost is taken to be in the same currency as the bid.';

/* ------------------------------------------------------------------ *
 * Price — what the auction uses as the gross value (slice `3c-i`)
 * ------------------------------------------------------------------ */

/**
 * How the price is stated (`ST3`). A closed union because we decide what price types exist; CPC and
 * CPA prices wait for direct deals (Todd).
 *
 * - `floor` — the **contractual minimum**. A bound, never a starting point: it does not take part in
 *   the chain that *chooses* a floor, it `max`es the chosen value afterwards (`RS5b`), and it discards
 *   this source's own under-floor bids (`RS5d`, `#1109`). It never widens into a pod-level floor.
 * - `fixed` — **the price**, agreed by both parties (`Q2`). It replaces the floor chain rather than
 *   being a rung in it: every impression carries it as `bidfloor` exactly, and every ad this source
 *   fills is recorded at it (`applyFixedCpm`). *Fixed* means *does not vary by price or volume*
 *   wherever it appears.
 */
export type DemandSourcePriceType = 'floor' | 'fixed';

export const DEMAND_SOURCE_PRICE_TYPES: readonly DemandSourcePriceType[] = Object.freeze(['floor', 'fixed']);

/** Todd's words for the two, 2026-09-16. */
export const DEMAND_SOURCE_PRICE_TYPE_LABELS: Readonly<Record<DemandSourcePriceType, string>> = Object.freeze({
  floor: 'CPM Floor',
  fixed: 'Fixed Price',
});

/** What the auction uses as a source's gross value. */
export interface DemandSourcePrice {
  type: DemandSourcePriceType;
  /** A CPM, in the same units as the bid. */
  value: number;
  /** ISO-4217. Recorded, not converted — {@link COST_CURRENCY_NOT_CONVERTED} holds for a price too. */
  currency: string;
}

/**
 * **The one read of a stored price.** `undefined` when none is stated, or when what is stored is not a
 * price: a `type` nobody declared or a value that is not a non-negative number states nothing, and
 * inventing a floor from it would assert a commercial position nobody took.
 *
 * There is no legacy shape to read here. `floorCpm` + `cpmIsFixed` are **folded** where documents
 * enter the store (`foldRetiredTermFields`), never read at runtime — the `#1135` rule.
 */
export function readDemandSourcePrice(price: unknown): DemandSourcePrice | undefined {
  if (!price || typeof price !== 'object') return undefined;
  const raw = price as { type?: unknown; value?: unknown; currency?: unknown };
  if (raw.type !== 'floor' && raw.type !== 'fixed') return undefined;
  const value = typeof raw.value === 'string' ? Number(raw.value) : raw.value;
  if (typeof value !== 'number' || !Number.isFinite(value) || value < 0) return undefined;
  const currency = typeof raw.currency === 'string' && raw.currency.trim() ? raw.currency.trim() : 'USD';
  return { type: raw.type, value, currency };
}

/**
 * The source's **fixed** price, or `undefined` when it has none.
 *
 * One reader, because the answer decides three different things — what `resolveBidFloor` returns,
 * what `applyBidFloorBounds` writes onto every impression, and what price an ad is recorded at — and
 * three copies of *is this price fixed* is how they come to disagree.
 */
export function fixedPriceOf(price: DemandSourcePrice | undefined): number | undefined {
  return price?.type === 'fixed' ? price.value : undefined;
}

/** The source's **contractual minimum**, or `undefined` when it states a fixed price or none. */
export function floorPriceOf(price: DemandSourcePrice | undefined): number | undefined {
  return price?.type === 'floor' ? price.value : undefined;
}

/** Why a price value **cannot be saved**, or `null` when it can. An invalid value, so it blocks. */
export function priceValueProblem(value: number | undefined): string | null {
  if (value === undefined) return null;
  if (!Number.isFinite(value) || value < 0) return 'A price must be zero or more.';
  return null;
}

/** The price as an operator reads it: `$15.00 fixed` or `$2.00 floor`. */
export function describeDemandSourcePrice(price: DemandSourcePrice): string {
  return `${formatCostMoney(price.value, price.currency)} ${price.type === 'fixed' ? 'fixed' : 'floor'}`;
}

/* ------------------------------------------------------------------ *
 * Weight — a boost to what a source's ads are worth in selection (`#937`)
 * ------------------------------------------------------------------ */

/**
 * **Weight is a signed percent that boosts or reduces a source's ads in selection, and does nothing
 * else** (ruled by Todd 2026-09-21, `#937`, for slice `3c-i`).
 *
 * The operator's sentence is *"I want to boost this source by 20%, or reduce it by 10%"*, so `20` and
 * `-10` are stored as typed and absent is no boost. It multiplies the **net** value — the bid less
 * what we pay the source — because that is what the optimizer ranks by, and a boost applied to the
 * gross bid would reward a source for costing us more.
 *
 * **Selection only.** Booked revenue, the source's floor and every report stay on the real value: a
 * boost is a preference among ads, not a claim that an ad earned more. So the result lives on its own
 * field (`BidRefDto.rankValue`), never on `netValue`, which reporting reads.
 *
 * It no longer orders requests. Until `3c-i` the same field sorted calls under the `weighted` request
 * strategy on a 1–100 scale; the order of calls is `#852`'s, and an old ordering weight is not a
 * boost, so the fold drops it rather than reading `5` as *+5%*.
 */
export function rankValueOf(netValue: number, weightPercent: number | undefined): number {
  if (weightPercent === undefined || !Number.isFinite(weightPercent) || weightPercent === 0) return netValue;
  return netValue * (1 + weightPercent / 100);
}

/**
 * Why a weight **cannot be saved**, or `null` when it can. Below −100% the multiplier turns negative,
 * which would rank a source's best ad last and its worst first — not a reduction anybody means.
 */
export function weightValueProblem(value: number | undefined): string | null {
  if (value === undefined) return null;
  if (!Number.isFinite(value)) return 'Weight must be a number.';
  if (value < -100) return 'Weight cannot reduce a source by more than 100%.';
  return null;
}

/**
 * Why a max-rounds value **cannot be saved**, or `null` when it can (`3c-ii`, `#1398`). Moved here from
 * the wizard's own check so every surface that authors terms refuses the same values.
 */
export function maxFetchRoundsProblem(value: number | undefined): string | null {
  if (value === undefined) return null;
  if (!Number.isInteger(value) || value < 1) return 'Max fetch rounds must be a whole number greater than 0.';
  return null;
}

/** The weight as an operator reads it: `+20%`, `−10%`. */
export function describeDemandSourceWeight(weightPercent: number): string {
  const sign = weightPercent > 0 ? '+' : weightPercent < 0 ? '−' : '';
  return `${sign}${formatNumber(Math.abs(weightPercent))}%`;
}

/**
 * The terms as they may be **stored**. Before `3c-0` a cost was a bare number beside a `model` that
 * nothing read; a stored document keeps that shape until it is next saved.
 */
export interface StoredDemandSourceTerms {
  /** What we pay the source. */
  cost?: DemandSourceCost | number | null;
  /** What the auction uses as the gross value. Read only through {@link readDemandSourcePrice}. */
  price?: DemandSourcePrice | null;
  /** The score boost, a signed percent. */
  weight?: number | null;
  /** How many times req-loop may call the source per pod. */
  maxFetchRounds?: number | null;
  /** Whether the source is required. Absent says nothing, so a tier above may answer. */
  required?: boolean | null;
  /** Pre-`3c-0`. Read only by {@link readDemandSourceCost}. */
  model?: string | null;
  /** Pre-`3c-0`, and **never read**: the console seeded it from `floorCpm`, so it is a price. */
  value?: number | null;
  currency?: string | null;
}

/**
 * **The one read of a stored cost**, for every reader on both sides.
 *
 * A structured `cost` is read as written. A pre-`3c-0` document is read as follows:
 * - `model: 'revShare'` means a rev-share whose number is `cost`, the only number the old block
 *   ever applied;
 * - any other model means a CPM of `cost`, which is what the engine charged it.
 *
 * The old `value` is **not read**: the console seeded it from `floorCpm`, so it holds a price, not a
 * cost (`#1108`).
 *
 * Returns `undefined` when no cost is stated. A bid from that source is valued at the full bid.
 */
export function readDemandSourceCost(terms: StoredDemandSourceTerms | null | undefined): DemandSourceCost | undefined {
  const cost = terms?.cost;
  if (cost && typeof cost === 'object') {
    const value = Number(cost.value);
    if (!Number.isFinite(value)) return undefined;
    return {
      type: cost.type === 'revShare' ? 'revShare' : 'cpm',
      value,
      currency: cost.currency?.trim() || 'USD',
    };
  }
  if (typeof cost !== 'number' || !Number.isFinite(cost)) return undefined;
  return {
    type: terms?.model?.trim() === 'revShare' ? 'revShare' : 'cpm',
    value: cost,
    currency: terms?.currency?.trim() || 'USD',
  };
}

/**
 * **What a bid is worth to us after paying the source**, which is what the optimizer ranks by.
 *
 * - `cpm`: the bid less the cost. Both are CPMs, so the result may be negative, and a negative
 *   value is kept because it is true.
 * - `revShare`: the bid less that percentage of it.
 *
 * The bid is already the agreed price for a fixed-price source (`applyFixedCpm`), so a rev-share
 * applies to the price that source is paid on.
 */
export function netValueOf(bid: number, cost: DemandSourceCost | undefined): number {
  if (!cost) return bid;
  if (cost.type === 'revShare') return bid - (bid * cost.value) / 100;
  return bid - cost.value;
}

/**
 * Why a cost value **cannot be saved**, or `null` when it can. This is an invalid value, not a missing
 * one, so it blocks.
 */
export function costValueProblem(type: DemandSourceCostType, value: number | undefined): string | null {
  if (value === undefined) return null;
  if (!Number.isFinite(value) || value < 0) return 'Cost must be zero or more.';
  if (type === 'revShare' && value > 100) return 'A rev-share is a percentage of the bid, so it cannot exceed 100%.';
  return null;
}

/**
 * **The Cost block's preview line** — what one price is worth to us after the cost, and whether an ad
 * at that price is served (`#1352`).
 *
 * A source with a fixed CPM has one price, so the line uses it (Todd, 2026-09-17: *"20% of \$15 is
 * worth \$12"*). Otherwise it uses an example bid. It runs {@link netValueOf}, the optimizer's own rule.
 */
export function describeCostPreview(cost: DemandSourceCost, fixedPrice: number | undefined, required: boolean): string {
  const price = fixedPrice ?? EXAMPLE_BID;
  const on =
    fixedPrice === undefined ? `a ${formatCostMoney(price, cost.currency)} bid` : formatCostMoney(price, cost.currency);
  const subject =
    cost.type === 'revShare'
      ? `${formatNumber(cost.value)}% of ${on}`
      : `${formatCostMoney(cost.value, cost.currency)} CPM against ${on}`;
  const net = netValueOf(price, cost);
  const worth = `${subject} is worth ${formatCostMoney(net, cost.currency)}`;
  if (net >= 0) return `${worth}.`;
  if (required) return `${worth}, and is served anyway because the source is required.`;
  return fixedPrice === undefined
    ? `${worth}, so a bid that low is never served.`
    : `${worth}, so its ads are never served.`;
}

/** The bid the preview values when the source has no fixed price. Round, so the arithmetic reads at a glance. */
const EXAMPLE_BID = 10;

/** The cost as an operator reads it: `$0.50 CPM` or `20% of the bid`. */
export function describeDemandSourceCost(cost: DemandSourceCost): string {
  if (cost.type === 'revShare') return `${formatNumber(cost.value)}% of the bid`;
  return `${formatCostMoney(cost.value, cost.currency)} CPM`;
}

/** An amount in a currency, the way the Cost block writes one: `$8.00`, `−$0.50`, `2.00 EUR`. */
export function formatCostMoney(amount: number, currency: string): string {
  const fixed = Math.abs(amount).toFixed(2);
  const sign = amount < 0 ? '−' : '';
  return currency === 'USD' ? `${sign}$${fixed}` : `${sign}${fixed} ${currency}`;
}

function formatNumber(value: number): string {
  return Number.isInteger(value) ? String(value) : String(Number(value.toFixed(4)));
}
